






















































































































import {
  defineComponent,
  onMounted,
  ref,
  watch,
  reactive,
  computed,
} from "@vue/composition-api";
import { useMachine } from "@xstate/vue";
import { AxiosResponse } from "axios";

import {
  GetSubscriptionTableRequest,
  ProjectDto,
  SubscriptionApi,
  VueTableResponseOfSubscriptionsTableDto,
} from "@/api";
import ExportComponent from "@/components/export/ExportComponent.vue";
import { DataTableHeader, DataOptions } from "vuetify";
import Store from "@/store";
import { formatCurrency } from "@/util/currency";
import {
  toLocalDateStringShort,
  toLocalDateTimeStringShort,
} from "@/util/date";
import i18n from "@/i18n";
import getGender from "@/util/GenderHelper";
import router from "@/router";
import requestListMachine from "./SubscriptionListMachine";

export default defineComponent({
  components: { ExportComponent },
  setup() {
    const { state, service } = useMachine(requestListMachine);
    const api = new SubscriptionApi();

    const search = ref("");
    const currentPage = ref(0);
    const isDownloading = ref(false);
    const exportData = ref<AxiosResponse<string>>();

    const response: VueTableResponseOfSubscriptionsTableDto = reactive({
      items: [],
      total: 0,
    });

    const selectedProject = computed<ProjectDto | null>(
      () => Store.getters["User/getSelectedProject"]
    );

    let options: DataOptions = reactive({
      groupBy: [],
      groupDesc: [],
      itemsPerPage: 10,
      multiSort: false,
      mustSort: false,
      page: 1,
      sortBy: ["lastName"],
      sortDesc: [false],
    });

    const headers: DataTableHeader[] = [
      {
        text: i18n.t("subscription.table.price").toString(),
        value: "subscriptionPrice",
      },
      {
        text: i18n.t("subscription.table.name").toString(),
        value: "lastName",
      },
      {
        text: i18n.t("subscription.table.first-name").toString(),
        value: "firstName",
      },
      {
        text: i18n.t("subscription.table.gender").toString(),
        value: "gender",
      },
      {
        text: i18n.t("subscription.table.birth-date").toString(),
        value: "birthDate",
      },
      {
        text: i18n.t("subscription.table.employee-number").toString(),
        value: "employeeNr",
      },
      {
        text: i18n.t("subscription.table.type").toString(),
        value: "isFirstSubscription",
      },
      {
        text: i18n.t("subscription.table.month").toString(),
        value: "renewalPeriod",
      },
      {
        text: i18n.t("subscription.table.start").toString(),
        value: "subscriptionStart",
      },
      {
        text: i18n.t("subscription.table.end").toString(),
        value: "subscriptionEnd",
      },
      {
        text: i18n.t("subscription.table.status").toString(),
        value: "status",
      },
      {
        text: "",
        value: "actions",
      },
    ];

    async function fetchResults() {
      if (!selectedProject.value?.projectId) return;

      try {
        const request: GetSubscriptionTableRequest = {
          sortBy: options.sortBy,
          descending: options.sortDesc,
          page: options.page,
          rowsPerPage: options.itemsPerPage,
          search: search.value,
          projectId: selectedProject.value.projectId,
        };

        const { data } = await api.subscriptionGetSubscriptionsExpensiveTable(
          request
        );
        response.items = data.items;
        response.total = data.total;
        service.send("RESOLVE");
      } catch (e) {
        service.send("REJECT");
      }
    }

    function updateOptions(opts: DataOptions) {
      options = opts;
      service.send("RELOAD");
    }

    function onExport() {
      if (!selectedProject.value?.projectId) return;

      isDownloading.value = true;

      api
        .subscriptionExportSubscriptionsExpensiveToExcel(
          { projectId: selectedProject.value.projectId },
          {
            responseType: "arraybuffer",
          }
        )
        .then((data) => {
          isDownloading.value = false;
          exportData.value = data;
        })
        .catch(() => {
          isDownloading.value = false;
        });
    }

    onMounted(async () => {
      service.subscribe((s) => {
        if (s.matches("loading")) {
          fetchResults();
        }
      });
    });

    watch(
      () => search.value,
      () => {
        currentPage.value = 1;
        fetchResults();
      }
    );

    watch(selectedProject, () => {
      // Return to dashboard if no access to this page.
      if (selectedProject.value?.expensiveSubscriptionCheckActive !== true) {
        router.push({ name: "dashboard" });
      }
      fetchResults();
    });

    return {
      selectedProject,
      headers,
      options,
      search,
      currentPage,
      state,
      response,
      formatCurrency,
      toLocalDateStringShort,
      toLocalDateTimeStringShort,
      updateOptions,
      isDownloading,
      exportData,
      onExport,
      getGender,
    };
  },
});
